import React, { useEffect, useState } from 'react';
import { Settings, AutoAwesomeOutlined } from '@mui/icons-material';
import { Box, Divider, IconButton, Tooltip, useTheme } from '@mui/material';
import { RecordVoiceOverOutlined } from '@mui/icons-material';

import { useBookingsContext } from '../../../contexts/BookingsContext';
import { useSessionContext } from '../../../contexts/SessionContext';
import { useAIDocumentEditorContext } from '../../../contexts/AIDocumentEditorContext';
import { useCopilotContext } from '../../../contexts/CopilotContext';
import { TabType } from '../../../global/interfaces';

import Images from '../../../components/Images';
import Chat from '../../../components/Chat';
import Copilot from '../../../components/Copilot';

import { COLORS } from '../../../utils/colors';
import aiSearchIcon from '../../../utils/assets/ai_search.svg';
import streetviewIcon from '../../../utils/assets/streetview.png';
import SectionCards from './SectionCards';
import HomeOwnerNotes from '../../../components/HomeownerNotes';
import GoogleMaps from '../../../components/GoogleMaps';
import { useViewsStyles } from './styles';
import AIToolsIcon from '../../../icons/AIToolsIcon';
import ImagesIcon from '../../../icons/ImagesIcon';
import HomeOwnerNotesIcon from '../../../icons/HomeOwnerNotesIcon';
import ChatIcon from '../../../icons/ChatIcon';
import MapsIcon from '../../../icons/MapsIcon';
import StreetIcon from '../../../icons/StreetIcon';
import KnowledgeBase from './KnowledgeBase';
import KnowledgeBaseIcon from '../../../icons/KnowledgeBaseIcon';
import WebSearchIcon from '../../../icons/WebSearchIcon';
import { IPromptCard } from '@homeporter/types';
import Transcript from '../../../components/Transcript';

const Panels = () => {
  const theme = useTheme();
  const styles = useViewsStyles();
  const [copilotHeight, setCopilotHeight] = useState<number>(35);
  const { context, userSettings } = useBookingsContext();

  const {
    hasUnreadChat,
    setHasUnreadChat,
    hasUpdatedImages,
    setHasUpdatedImages,
    chatHistory,
  } = useSessionContext();

  const { highlightedSids } = useAIDocumentEditorContext();

  const { documentSection, activeTab, setActiveTab } =
    useAIDocumentEditorContext();

  useEffect(() => {
    setActiveTab(!context ? 'Appointment Description' : 'Cards');
  }, [context]);

  const { copilotEnabled, setCopilotEnabled } = useCopilotContext();

  const handleSetActiveTab = (label: TabType) => {
    if (label === 'Copilot') {
      setCopilotEnabled(!copilotEnabled);
      return;
    }

    setActiveTab(label);
    if (label === 'Chat') {
      setHasUnreadChat(false);
    }
    if (label === 'Images') {
      setHasUpdatedImages(false);
    }
  };

  useEffect(() => {
    setActiveTab('Cards');
  }, [highlightedSids]);

  const [editButton, setEditButton] = useState<boolean>(false);

  useEffect(() => {
    const _editButton = !!documentSection?.cards.some(
      (item) =>
        (item as IPromptCard).modified || (item as IPromptCard).response,
    );

    setEditButton(_editButton);
  }, [documentSection?.cards]);

  const RIGHT_SIDEBAR_ICONS = [
    ...(userSettings?.dev
      ? [
          {
            Icon: AutoAwesomeOutlined,
            label: 'Copilot',
            viewTitle: 'AI Search',
            view: true,
          },
        ]
      : []),
    {
      Icon: AIToolsIcon,
      label: 'Cards',
      viewTitle: 'Cards',
      view: <SectionCards viewTitle={'Cards'} />,
    },
    {
      Icon: RecordVoiceOverOutlined,
      label: 'Transcripts',
      viewTitle: 'Transcript',
      view: <Transcript useTabs={true} viewTitle={'Transcripts'} />,
    },
    {
      Icon: ImagesIcon,
      label: 'Images',
      viewTitle: 'Images',
      view: <Images viewTitle={'Images'} />,
    },
    {
      Icon: HomeOwnerNotesIcon,
      label: 'Appointment Description',
      viewTitle: 'Appointment Description',
      view: (
        <HomeOwnerNotes
          useTabs={false}
          viewTitle={'Appointment Description'}
          editButton={editButton}
        />
      ),
    },
    ...(true || userSettings?.dev
      ? [
          {
            Icon: KnowledgeBaseIcon,
            label: 'Knowledge Base',
            viewTitle: 'Knowledge Base',
            view: <KnowledgeBase />,
          },
        ]
      : []),
    /*    {
      Icon: WebSearchIcon,
      label: 'WebSearch',
      viewTitle: 'Web Search',
      view: false,
    },*/
    ...(chatHistory.length > 0
      ? [
          {
            Icon: ChatIcon,
            label: 'Chat',
            viewTitle: 'Chat with HomeOwner',
            view: <Chat useTabs={false} viewTitle={'Chat'} />,
          },
        ]
      : []),
    {
      Icon: MapsIcon,
      label: 'Map',
      viewTitle: 'Map',
      view: <GoogleMaps viewTitle={'Map'} />,
    },
  ];

  useEffect(() => {
    if (hasUnreadChat && activeTab === 'Chat') {
      setHasUnreadChat(false);
    }

    if (hasUpdatedImages && activeTab === 'Images') {
      setHasUpdatedImages(false);
    }
  }, [
    activeTab,
    hasUnreadChat,
    hasUpdatedImages,
    setHasUnreadChat,
    setHasUpdatedImages,
  ]);

  useEffect(() => {
    if (highlightedSids.length > 0) setActiveTab('Transcripts');
  }, [highlightedSids]);

  return (
    <Box sx={styles.view}>
      <Box sx={styles.vhcInfo}>
        {copilotEnabled && (
          <Copilot
            viewTitle={'Dev Copilot'}
            copilotHeight={copilotHeight}
            setCopilotHeight={setCopilotHeight}
          />
        )}
        {RIGHT_SIDEBAR_ICONS.map(({ label, view, viewTitle }, index) => {
          const active = label === activeTab && view;
          return !active ? undefined : (
            <Box
              key={`sidebar-${index}`}
              height={`${copilotEnabled ? 100 - copilotHeight : 100}%`}>
              {view}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          ...styles.sidebar,
          boxShadow: '0 2px 9px 0 rgba(0, 0, 0, 0.15)',
          bgcolor: COLORS.WHITE,
        }}>
        {RIGHT_SIDEBAR_ICONS.map(({ Icon, label, view }, index) => {
          const isDisabled = !view || label === 'Streetview';
          const active =
            activeTab === label || (label === 'Copilot' && copilotEnabled);
          const bgColor = active ? COLORS.LIGHT_GRAY : COLORS.WHITE;

          return (
            <React.Fragment key={`panel-${index}`}>
              <Box
                sx={{
                  ...styles.sidebarButton,
                  color: active ? 'black' : 'black',
                }}
                bgcolor={bgColor}
                borderColor={
                  label === 'Transcripts'
                    ? `${theme.palette.primary}`
                    : 'transparent'
                }
                borderBottom={
                  label === 'Chat'
                    ? `5px solid ${COLORS.LIGHT_GRAY}`
                    : 'transparent'
                }>
                <Tooltip title={label} placement="left">
                  <IconButton
                    id={`_${label.toLowerCase()}`}
                    disabled={isDisabled}
                    disableRipple
                    onClick={() => handleSetActiveTab(label as TabType)}
                    color="inherit">
                    {((label === 'Chat' && hasUnreadChat) ||
                      (label === 'Images' && hasUpdatedImages)) && (
                      <Box sx={styles.unreadMessageIndicator} />
                    )}

                    {label === 'AI Search' || label === 'Streetview' ? (
                      <img
                        alt={label}
                        src={
                          label === 'AI Search' ? aiSearchIcon : streetviewIcon
                        }
                        style={{
                          objectFit: 'contain',
                          opacity: isDisabled ? 0.5 : 1,
                          width: '2rem',
                          height: '2rem',
                        }}
                      />
                    ) : (
                      <Icon sx={{ fontSize: '25px', color: '#888' }} />
                    )}
                  </IconButton>
                </Tooltip>
                {label === 'Chat' && (
                  <span
                    style={{
                      display: 'block',
                      height: '10px',
                      width: '100%',
                      backgroundColor: COLORS.LIGHT_GRAY,
                    }}></span>
                )}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default Panels;
